import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "../UserManagement/UserManagement.scss";
import searchIconIMG from "../../assets/images/megnificant-glass.png";
import filterIcon from "../../assets/images/filter-icon.svg";
import CommonTable from "../../components/Table/CommonTable";
import avatar1 from "../../assets/images/user1.png";
import eye from "../../assets/images/eye.svg";
import restriction from "../../assets/images/restriction.svg";
import checked from "../../assets/images/checked.svg";
import deleteIcon from "../../assets/images/delete.png";
import AddUser from "./AddUser";
import UserDetails from "./UserDetails";
import { useNavigate } from "react-router-dom";
import { TIME_LOGS, USER_MANAGEMENT } from "../../utils/const";
import useApi from "../../hook/useAPI";
import UserStatusModal from "./UserStatusModal";
import DeleteUserModal from "./DeleteUserModal";
import { debounce } from 'lodash';
import { useContextValue } from "../../context/LoaderContext";
import { profile } from "../../constants/Constant";
import {
  USER_ACCESS_FILTER_GROUPS,
  getAccessFilterLabel,
} from "../../constants/adminUserFilters";

const buildUserListQuery = ({
  page,
  limit,
  search,
  status,
  accessFilter,
}) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  params.set('search', search || '');
  if (status?.toLowerCase()) {
    params.set('status', status.toLowerCase());
  }
  if (accessFilter.length > 0) {
    params.set('access', accessFilter.join(','));
  }
  return `${USER_MANAGEMENT.GET_ALL_USER}?${params.toString()}`;
};

const UserManagement = () => {
  const [detailsmodalShow, setDetailsModalShow] = useState(false);
  const [addmodalShow, setAddModalShow] = useState(false);
  const navigate = useNavigate();
  const [statusmodalShow, setStatusModalShow] = useState(false);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredUserDetails, setFilteredUserDetails] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatchingUsers, setTotalMatchingUsers] = useState(0);
  const { userDetails } = useContextValue();
  const [status, setStatus] = useState("");
  const [accessFilter, setAccessFilter] = useState([]);
  const [tempStatus, setTempStatus] = useState("");
  const [tempAccessFilter, setTempAccessFilter] = useState([]);
  const { getData, deleteData } = useApi();

  const hasActiveFilters = Boolean(
    searchQuery.trim() || status || accessFilter.length > 0,
  );
  const showDeleteMatching = hasActiveFilters && totalMatchingUsers > 0;

  const formatTrialDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const handleAddUser = () => {
    setAddModalShow(true);
  };

  const handleUserDetails = (user) => {
    setSelectedUser(user);
    setDetailsModalShow(true);
  };

  const handleViewTimeLogs = (user) => {
    navigate(`${TIME_LOGS.PAGE}?userId=${user._id}`);
  };
  const handleUserStatus = (user) => {
    setSelectedUser(user);
    setStatusModalShow(true);
  };
  const handleDeleteRequest = (user) => {
    setSelectedUser(user);
    setDeleteModalShow(true);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedSearch(value);
  };

  const handleStatusChangeTemp = (event) => {
    setTempStatus(event.target.value);
  };

  const handleAccessFilterToggle = (value) => {
    setTempAccessFilter((prev) => (
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    ));
  };

  const removeAccessFilterChip = (value) => {
    const nextAccessFilter = accessFilter.filter((item) => item !== value);
    setAccessFilter(nextAccessFilter);
    setTempAccessFilter((prev) => prev.filter((item) => item !== value));
    setCurrentPage(1);
    loadUsers({ page: 1, nextAccessFilter });
  };

  const clearStatusFilter = () => {
    setStatus('');
    setTempStatus('');
    setCurrentPage(1);
    loadUsers({ page: 1, nextStatus: '' });
  };

  const ActionCoulmn = (user) => {
    const statusIcon = user.status === "active" ? restriction : checked;
    return (
      <div className="action-control-btn">
        <button
          className="action-btn"
          title="Time logs"
          onClick={() => handleViewTimeLogs(user)}
        >
          <i className="bi bi-clock-history" aria-hidden="true" />
        </button>
        <button className="action-btn" onClick={() => handleUserDetails(user)}>
          <img src={eye} alt="eye" />
        </button>
        <button className="action-btn" onClick={() => handleUserStatus(user)}>
          <img src={statusIcon} alt={`${statusIcon}`} />
        </button>
        <button className="action-btn" onClick={() => handleDeleteRequest(user)}>
          <img src={deleteIcon} alt="delete" />
        </button>
      </div>
    );
  };

  const handleDeleteAllMatching = async () => {
    if (!hasActiveFilters) {
      return;
    }

    const confirmed = window.confirm(
      'Delete all users matching the current search and filters? This cannot be undone.',
    );
    if (!confirmed) {
      return;
    }

    try {
      const params = new URLSearchParams();
      params.set('search', searchQuery || '');
      if (status?.toLowerCase()) {
        params.set('status', status.toLowerCase());
      }
      if (accessFilter.length > 0) {
        params.set('access', accessFilter.join(','));
      }
      const response = await deleteData(`${USER_MANAGEMENT.BULK_DELETE_USERS}?${params.toString()}`);
      if (response?.status) {
        loadUsers({ search: searchQuery, page: 1 });
      }
    } catch (error) {
      console.error('Error deleting matching users:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?._id) {
      return;
    }

    try {
      const response = await deleteData(`${USER_MANAGEMENT.DELETE_USER}/${selectedUser._id}`);
      if (response?.status) {
        loadUsers({ search: searchQuery });
      }
      setDeleteModalShow(false);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const loadUsers = useCallback(async ({
    search = searchQuery,
    page = currentPage,
    limit = itemsPerPage,
    nextStatus = status,
    nextAccessFilter = accessFilter,
  } = {}) => {
    try {
      const response = await getData(buildUserListQuery({
        page,
        limit,
        search,
        status: nextStatus,
        accessFilter: nextAccessFilter,
      }));
      const data = response;
      const users = Array.isArray(data?.users) ? data.users : [];
      const total = data?.total != null && !Number.isNaN(Number(data.total))
        ? Number(data.total)
        : users.length;
      const processedData = users.map((user) => ({
        userName: { value: user?.firstname + ' ' + user?.lastname, img: user?.profileFileName ? profile + user?.profileFileName: avatar1},
        email: user?.email,
        status: user?.status === "active" ? "Active" : "Inactive",
        plan: user?.trial?.isActive
          ? `Trial (ends ${formatTrialDate(user?.trial?.endsAt)})`
          : (user?.subscribedPlanLabel || user?.effectivePlan?.planName || user?.plan?.planName || '-'),
        action: <>{ActionCoulmn(user)}</>,
      }));
      setFilteredUserDetails(processedData);
      setTotalMatchingUsers(total);
      setTotalPages(total > 0 ? Math.max(1, Number(data.totalPages) || 1) : 0);
      setCurrentPage(Number(data.currentPage) || page);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [currentPage, itemsPerPage, status, accessFilter, searchQuery, getData]);

  const debouncedSearch = useMemo(
    () => debounce((search) => {
      loadUsers({ search, page: 1 });
    }, 500),
    [loadUsers],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleClose = () => {
    setDetailsModalShow(false);
    setAddModalShow(false);
    setStatusModalShow(false);
    setDeleteModalShow(false);
  };

  const handleUserCreated = () => {
    setAddModalShow(false);
    loadUsers({ search: searchQuery });
  };

  const toggleFilterPanel = () => {
    if (!isFilterPanelOpen) {
      setTempStatus(status);
      setTempAccessFilter([...accessFilter]);
    }
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  const closeFilterPanel = () => {
    setIsFilterPanelOpen(false);
  };

  const columnsOptions = [
    { header: "User Name", key: "userName" },
    { header: "Email", key: "email" },
    { header: "Status", key: "status" },
    { header: "Access", key: "plan" },
    { header: "Action", key: "action" },
  ];

  const applyFilters = () => {
    const nextStatus = tempStatus;
    const nextAccessFilter = [...tempAccessFilter];
    debouncedSearch.cancel();
    setStatus(nextStatus);
    setAccessFilter(nextAccessFilter);
    setCurrentPage(1);
    setIsFilterPanelOpen(false);
    loadUsers({
      search: searchQuery,
      page: 1,
      nextStatus,
      nextAccessFilter,
    });
  };

  const resetFilters = () => {
    setTempStatus("");
    setTempAccessFilter([]);
    setStatus("");
    setAccessFilter([]);
    setCurrentPage(1);
    debouncedSearch.cancel();
    loadUsers({
      search: searchQuery,
      page: 1,
      nextStatus: "",
      nextAccessFilter: [],
    });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    debouncedSearch.cancel();
    setTempStatus("");
    setTempAccessFilter([]);
    setStatus("");
    setAccessFilter([]);
    setCurrentPage(1);
    loadUsers({
      search: '',
      page: 1,
      nextStatus: "",
      nextAccessFilter: [],
    });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleStatusUpdated = (userId, newStatus) => {
    if (Array.isArray(userDetails)) {
      const updatedUserDetails = userDetails.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      );
      setFilteredUserDetails(updatedUserDetails);
      setStatusModalShow(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [currentPage, itemsPerPage, statusmodalShow]);

  const activeFilterCount = (status ? 1 : 0) + accessFilter.length;

  useEffect(() => {
    if (!isFilterPanelOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeFilterPanel();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isFilterPanelOpen]);

  const renderFilterPanel = () => (
    <div
      className="filter-panel filter-panel--access filter-panel--sheet"
      role="dialog"
      aria-modal="true"
      aria-label="User filters"
    >
      <div className="filter-panel-header">
        <div className="filter-nav-title">
          <img src={filterIcon} alt="" />
          Filters
        </div>
        <button
          type="button"
          className="close-panel-btn"
          onClick={closeFilterPanel}
          aria-label="Close filters"
        >
          <span className="close-panel-btn__icon" aria-hidden="true">×</span>
        </button>
      </div>

      <div className="filter-panel-scroll">
        <p className="filter-panel-intro">
          Filters use the same rules as the <strong>Access</strong> column.
          Combine groups with AND; options inside a group use OR.
        </p>

        <div className="status-filter-block subscribed-filter-block">
          <legend className="col-form-label">Account status</legend>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="userStatusFilter"
              id="userStatusActive"
              value="Active"
              checked={tempStatus === "Active"}
              onChange={handleStatusChangeTemp}
            />
            <label className="form-check-label" htmlFor="userStatusActive">
              Active
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="userStatusFilter"
              id="userStatusInactive"
              value="Inactive"
              checked={tempStatus === "Inactive"}
              onChange={handleStatusChangeTemp}
            />
            <label className="form-check-label" htmlFor="userStatusInactive">
              Inactive
            </label>
          </div>
          <button
            type="button"
            className="btn btn-link btn-sm filter-clear-status"
            onClick={() => setTempStatus('')}
          >
            Any status
          </button>
        </div>

        {USER_ACCESS_FILTER_GROUPS.map((group) => (
          <div key={group.id} className="status-filter-block filter-group-block">
            <legend className="col-form-label">{group.title}</legend>
            <p className="filter-group-hint">{group.hint}</p>
            {group.options.map((option) => (
              <div className="form-check filter-option-check" key={option.value}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`access-filter-${option.value}`}
                  value={option.value}
                  checked={tempAccessFilter.includes(option.value)}
                  onChange={() => handleAccessFilterToggle(option.value)}
                />
                <label className="form-check-label" htmlFor={`access-filter-${option.value}`}>
                  <span className="filter-option-label">{option.label}</span>
                  <span className="filter-option-desc">{option.description}</span>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="filter-panel-footer">
        <button type="button" className="btn btn-primary cancel-btn" onClick={resetFilters}>
          Reset
        </button>
        <button type="button" className="btn btn-primary" onClick={applyFilters}>
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="dashboard user-management-page">
        <div className="row">
          <div className="col-12 col-lg-12 col-xl-12">
            <div className="card card-table-wrap">
              <div className="card-body p-0">
                <div className="home-table-chart-box user-mgmt-table-wrap">
                  <div className="top_bar user-mgmt-top-bar">
                    <div className="user-mgmt-top-bar__left">
                      <div className="card_title">User Management</div>
                      {hasActiveFilters && (
                        <div className="user-filter-chips" aria-label="Active filters">
                          {searchQuery.trim() && (
                            <span className="user-filter-chip">
                              Search: {searchQuery.trim()}
                            </span>
                          )}
                          {status && (
                            <span className="user-filter-chip">
                              Status: {status}
                              <button type="button" className="user-filter-chip-remove" onClick={clearStatusFilter} aria-label="Remove status filter">×</button>
                            </span>
                          )}
                          {accessFilter.map((token) => (
                            <span className="user-filter-chip" key={token}>
                              {getAccessFilterLabel(token)}
                              <button
                                type="button"
                                className="user-filter-chip-remove"
                                onClick={() => removeAccessFilterChip(token)}
                                aria-label={`Remove ${getAccessFilterLabel(token)} filter`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {showDeleteMatching && (
                            <button
                              type="button"
                              className="btn btn-delete-matching btn-delete-matching--chip"
                              onClick={handleDeleteAllMatching}
                            >
                              Delete all matching ({totalMatchingUsers})
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="top_bar_right user-mgmt-toolbar">
                      <div className="input-group user-mgmt-search">
                        <span className="input-group-text" id="basic-addon1">
                          <img src={searchIconIMG} className="img-fluid" alt="search-icon" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search name, email, status"
                          aria-label="Search"
                          aria-describedby="basic-addon1"
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                      </div>

                      <div className="user-mgmt-toolbar__actions">
                      <div className="user-filter-wrap">
                        <button
                          type="button"
                          className={`btn filter-toggle${activeFilterCount > 0 ? ' filter-toggle--active' : ''}`}
                          onClick={toggleFilterPanel}
                          aria-expanded={isFilterPanelOpen}
                          aria-haspopup="dialog"
                        >
                          <img src={filterIcon} alt="filterIcon" />
                          {activeFilterCount > 0 && (
                            <span className="filter-toggle-badge">{activeFilterCount}</span>
                          )}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-primary-user"
                        onClick={handleAddUser}
                      >
                        Add User
                      </button>
                      </div>
                    </div>
                  </div>

                  <CommonTable
                    columns={columnsOptions}
                    rows={filteredUserDetails}
                    itemsPerPage={itemsPerPage}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    totalCount={totalMatchingUsers}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={handlePageChange}
                    isPagination
                    pageName="user-management"
                    showHeaderWhenEmpty
                    hidePaginationWhenEmpty
                    emptyState={{
                      title: hasActiveFilters ? 'No users match these filters' : 'No users yet',
                      message: hasActiveFilters
                        ? 'Try adjusting your search or clearing filters to see more users.'
                        : 'Add a user to get started with your team.',
                      actionLabel: hasActiveFilters ? 'Clear all filters' : undefined,
                      onAction: hasActiveFilters ? clearAllFilters : undefined,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddUser show={addmodalShow} handleClose={handleClose} onUserCreated={handleUserCreated} />
      <UserDetails show={detailsmodalShow} handleClose={handleClose} userId={selectedUser?._id} />
      <UserStatusModal
        show={statusmodalShow}
        handleClose={handleClose}
        userId={selectedUser?._id}
        currentStatus={selectedUser?.status}
        onStatusUpdated={(newStatus) => handleStatusUpdated(selectedUser?._id, newStatus)}
      />
      <DeleteUserModal
        show={deleteModalShow}
        handleClose={handleClose}
        user={selectedUser}
        onConfirm={handleDeleteUser}
      />

      {isFilterPanelOpen && createPortal(
        <div className="user-management-filter-portal">
          <button
            type="button"
            className="user-filter-backdrop"
            onClick={closeFilterPanel}
            aria-label="Close filters"
          />
          {renderFilterPanel()}
        </div>,
        document.body,
      )}
    </>
  );
};

export default UserManagement;
